import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from '../ui/Button';
import alertService from '../../services/alertService';

const ComplianceAlertBanner = () => {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [isDismissed, setIsDismissed] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAlerts();
    
    // Subscribe to real-time alerts
    const channel = alertService?.subscribeToAlerts((newAlert) => {
      if (newAlert?.severity === 'critical' || newAlert?.severity === 'high') {
        setAlerts(prev => [newAlert, ...prev]);
        setIsDismissed(false);
      }
    });

    const dismissed = sessionStorage.getItem('complianceAlertDismissed');
    if (dismissed) {
      setIsDismissed(true);
    }

    return () => {
      alertService?.unsubscribeFromAlerts(channel);
    };
  }, []);

  const loadAlerts = async () => {
    try {
      setLoading(true);
      const data = await alertService?.getUnreadAlerts();
      const criticalAlerts = data?.filter(a => a?.severity === 'critical' || a?.severity === 'high') || [];
      setAlerts(criticalAlerts);
    } catch (err) {
      console.error('Failed to load alerts:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    setIsDismissed(true);
    sessionStorage.setItem('complianceAlertDismissed', 'true');
  };

  const handleViewDetails = () => {
    const alert = alerts?.[0];
    if (alert?.alertType === 'nasfund_deadline' || alert?.alertType === 'swt_filing_error') {
      navigate('/payroll-compliance');
    } else if (alert?.alertType === 'compliance_violation') {
      navigate('/compliance-overview');
    } else if (alert?.alertType === 'payroll_approval_status') {
      navigate('/payroll-management');
    } else {
      navigate('/compliance-overview');
    }
  };

  const getSeverityStyles = (severity) => {
    switch (severity) {
      case 'critical':
        return {
          bg: 'bg-error',
          text: 'text-error-foreground',
          icon: 'AlertTriangle'
        };
      case 'warning':
        return {
          bg: 'bg-warning',
          text: 'text-warning-foreground',
          icon: 'AlertCircle'
        };
      default:
        return {
          bg: 'bg-muted',
          text: 'text-muted-foreground',
          icon: 'Info'
        };
    }
  };

  if (loading || alerts?.length === 0 || isDismissed) {
    return null;
  }

  const alert = alerts?.[0];
  const styles = getSeverityStyles(alert?.severity);

  return (
    <div className={`${styles?.bg} ${styles?.text} px-6 py-4 z-1100 transition-smooth`}>
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
        <div className="flex items-center gap-4 flex-1">
          <div className="pulse-alert">
            <Icon name={styles?.icon} size={24} color="currentColor" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h4 className="font-heading font-semibold text-base">{alert?.title}</h4>
              {alert?.affectedCount > 0 && (
                <span className="px-2 py-0.5 text-xs font-caption bg-card/20 rounded-md">
                  {alert?.affectedCount} affected
                </span>
              )}
            </div>
            <p className="text-sm font-body opacity-90">{alert?.message}</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleViewDetails}
            className="bg-card/10 border-current hover:bg-card/20"
          >
            View Details
          </Button>
          <button
            onClick={handleDismiss}
            className="flex items-center justify-center w-8 h-8 rounded-md hover:bg-card/20 transition-smooth"
            aria-label="Dismiss alert"
          >
            <Icon name="X" size={20} color="currentColor" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ComplianceAlertBanner;